'use client';

import { useMemo, useEffect, useState, useRef } from 'react';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation, Container, Text, Image, Link } from '@weareconceptstudio/core';

//* Libs
import { getRecipesRelatedProduct } from '@/libs/get-data';

//* Helpers
import { config } from '@/helpers';

//* Styles
import CookingStyle from './style';

//* Components
import StyledQuotedText from '@/components/StyledQuotedText';
import Button from '@/components/Button';
import RecipesItem from '@/modules/Recipes/components/List/Item';
import TitleAnim from '@/modules/_animation/Title';

gsap.registerPlugin(ScrollTrigger);

const Cooking = ({ id }) => {
	const { selectedLang } = useTranslation();

	//! Ref
	const titleWrapRef = useRef();
	const triggerRef = useRef();
	const reverseTrigger = useRef();
	const cookieRef = useRef();
	const buttonRefWrap = useRef();

	//! State
	const [data, setData] = useState([]);

	useEffect(() => {
		const fetchData = async () => {
			const data = await getRecipesRelatedProduct({ id, lang: selectedLang });

			setData(data);
		};

		fetchData();
	}, [id, selectedLang]);

	const dataCookings = useMemo(() => {
		return data?.map((item) => (
			<RecipesItem
				key={item.id}
				{...item}
			/>
		));
	}, [data]);

	useEffect(() => {
		if (titleWrapRef.current) {
			triggerRef.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top top',
				trigger: titleWrapRef.current,
				// markers: true,
				onEnter: () => {
					if (cookieRef.current?.children) {
						gsap.fromTo(buttonRefWrap.current, { scale: 0 }, { scale: 1, duration: 0.6, ease: 'power2.inOut' });

						gsap.fromTo([...cookieRef.current.children], { y: '100%' }, { y: 0, duration: 0.6, ease: 'power2.out', stagger: 0.1, delay: 0.1 });
						gsap.fromTo([...cookieRef.current.children], { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.in', stagger: 0.1 });
					}
				},
				onLeaveBack: () => {
					if (cookieRef.current?.children) {
						gsap.set(buttonRefWrap.current, { scale: 0 });

						gsap.set([...cookieRef.current.children], { y: '100%' });
						gsap.set([...cookieRef.current.children], { opacity: 0 });
					}
				},
			});
		}

		return () => {
			if (triggerRef.current) {
				triggerRef.current.kill();
			}
		};
	});

	return (
		<CookingStyle>
			<Container
				row
				isSection
			>
				<div className={'col-1 col-ds-12'} />

				<div className={'col-10 col-ds-12 col-m-12'}>
					{dataCookings?.length > 0 && (
						<>
							<div className='wrapper-related-titles'>
								<div ref={titleWrapRef}>
									<TitleAnim load={true}>
										<StyledQuotedText text={'recipesWith" ""oraBlend"'} />
									</TitleAnim>
								</div>
								<div
									ref={buttonRefWrap}
									className='wrapper-button'
								>
									<Button
										url={`${config.routes.recipes.path}`}
										text={'viewAll'}
										className={'show-more-btn'}
									/>
								</div>
							</div>

							<div className='wrapper-items'>
								<div
									ref={cookieRef}
									className={'wrapper-cookings'}
								>
									{dataCookings}
								</div>
							</div>
						</>
					)}
				</div>

				<div className={'col-1 col-ds-12'} />
			</Container>
		</CookingStyle>
	);
};

export default Cooking;
